import React from 'react';
import PropTypes from 'prop-types';
import Formsy from 'formsy-react';
import Cookies from 'js-cookie';
import cx from 'classnames';

import helpers from '../../resources/helpers';
import webNodeHelper from '../../resources/webnode-helper';

import HiddenInput from '../form-elements/HiddenInput';

import './EsriForm.scss';

class EsriForm extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			isValid: false,
			inValidSubmit: false,
			formStarted: false,
		};

		this.form = React.createRef();

		this.handleFormStart = this.handleFormStart.bind(this);
		this.handleValid = this.handleValid.bind(this);
		this.handleInvalid = this.handleInvalid.bind(this);
		this.handleInvalidSubmit = this.handleInvalidSubmit.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
		this.handleSubmissionError = this.handleSubmissionError.bind(this);
		this.handleChange = this.handleChange.bind(this);
		this.reset = this.reset.bind(this);
	}

	async componentDidMount() {
		let analyticsProps = {};
		if (!this.props.disableAnalytics) {
			analyticsProps = await helpers.initFormAnalytics({
				formName: this.props.formName || `${window.document.title} Form`,
				analyticsProps: this.props.analyticsProps,
			});
		}
		if (this.props.onFormLoad) {
			this.props.onFormLoad(analyticsProps);
		}
	}

	async handleFormStart() {
		if (!this.state.formStarted) {
			let analyticsProps = {};
			if (!this.props.disableAnalytics) {
				analyticsProps = await helpers.trackFormStart({ analyticsProps: this.props.analyticsProps });
			}
			if (this.props.onFormStart) {
				this.props.onFormStart(analyticsProps);
			}
			this.setState({ formStarted: true });
		}
	}

	handleValid() {
		this.setState(
			{
				isValid: true,
				inValidSubmit: false,
			},
			() => {
				if (this.props.onValid) {
					const formData = this.form.current.getModel();
					this.props.onValid(formData);
				}
			}
		);
	}

	handleInvalid() {
		this.setState(
			{
				isValid: false,
			},
			() => {
				if (this.props.onInvalid) {
					const formData = this.form.current.getModel();
					this.props.onInvalid(formData);
				}
			}
		);
	}

	handleChange() {
		if (this.props.onChange) {
			const formData = this.form.current.getModel();
			delete formData.btst;

			this.props.onChange(formData);
		}
	}

	handleInvalidSubmit() {
		this.setState(
			{
				isValid: false,
				inValidSubmit: true,
			},
			() => {
				if (this.props.onInvalidSubmit) {
					const formData = this.form.current.getModel();
					this.props.onInvalidSubmit(formData);
				}
			}
		);
	}

	async handleSubmit(formData) {
		await this.handleFormStart();
		try {
			this.setState({ inValidSubmit: false }, async () => {
				//formData.btst is a hidden honeypot field - it should never have a value
				if (!formData.btst) {
					let workingFormData = { ...formData };
					delete workingFormData.btst;

					if (!this.props.disableAnalytics) {
						const analyticsProps = await helpers.trackFormSubmit({
							analyticsProps: this.props.analyticsProps,
							isMQL: this.props.isMql,
						});
						workingFormData.marketingCloudVisitorID = analyticsProps.marketingCloudVisitorID;
						workingFormData.originatorUrl = analyticsProps.originatorUrl;
						workingFormData.submissionId = analyticsProps.submissionID;
					}

					if (formData.hasGDPR && formData.marketingConsent) {
						const d = new Date();
						formData.marketingConsent = 'yes';
						formData.marketingConsentDate = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
						delete formData.hasGDPR;

						Cookies.set('gdpr_form_accepted', 'true', { secure: window.location.protocol === 'https:', expires: 365 });
					}

					if (!workingFormData.originatorUrl) {
						try {
							workingFormData.originatorUrl = window.top.location.href.split('?')[0].split('#')[0];
						} catch (err) {
							workingFormData.originatorUrl = window.location.href.split('?')[0].split('#')[0];
						}
					}

					workingFormData.clientIp = await helpers.getClientIP();
					workingFormData.langCode = window.document.documentElement.lang || 'en';
					workingFormData.language = helpers.getPageLanguage();

					if (this.props.onSubmit) {
						try {
							const updatedFormData = await this.props.onSubmit({ ...workingFormData });
							if (updatedFormData) {
								workingFormData = updatedFormData;
							}
						} catch (err) {
							return this.handleSubmissionError(err);
						}
					}

					const sfId = helpers.getSFId();
					const pardotUrl = this.lookupPardotUrl(sfId);

					if (this.props.forceWebNodeSubmission || pardotUrl) {
						workingFormData.pardotHandlerUrl = pardotUrl;
						workingFormData.isMql = this.props.isMql;
						workingFormData.sfId = sfId || 'organic';
						if (window.dataLayer) {
							window.dataLayer.formInfo = {
								...window.dataLayer.formInfo,
								isMQL: formData.isMql,
							};
						}

						try {
							const response = await webNodeHelper.submitToPardot(workingFormData);
							if (response.indexOf('error') === -1) {
								await this.setPardotCookie(workingFormData.email);
							}
						} catch (err) {
							return this.handleSubmissionError(err);
						}
					}

					if (this.props.onAfterSubmit) {
						await this.props.onAfterSubmit(workingFormData);
					}
				}
			});
		} catch (err) {
			return this.handleSubmissionError(err);
		}
	}

	handleSubmissionError(err) {
		console.error(err);
		if (this.props.onSubmitError) {
			this.props.onSubmitError(err);
		}
	}

	lookupPardotUrl(sf_id) {
		const sfIdOverrides = this.props.pardotSFIDFormHandlerLookup || [];
		const sfIdOverride = sfIdOverrides.filter((ovr) => ovr.sfId === sf_id)[0];
		const pardotUrl = sfIdOverride ? sfIdOverride.url : this.props.pardotDefaultFormHandler;
		return pardotUrl;
	}

	async setPardotCookie(email) {
		await helpers.createIframe(`https://go.esri.com/l/82202/2018-10-04/lxz4sn?email=${email}`);
		await helpers.createIframe(`https://go.pardot.com/l/82202/2018-10-04/lxz4sn?email=${email}`);
	}

	renderChildren() {
		const children = Array.isArray(this.props.children) ? this.props.children : [this.props.children];
		const propsForChildren = {
			formHasHadAnInValidSubmit: this.state.inValidSubmit,
			onFocus: this.handleFormStart,
		};
		return children.map(applyPropsForChildren);

		function applyPropsForChildren(elem, idx) {
			let enhancedElem;
			let children = elem && elem.props ? elem.props.children : undefined;

			if (children) {
				if (Array.isArray(children)) {
					children = children.map(applyPropsForChildren);
				} else {
					children = applyPropsForChildren(children, 0);
				}
			}

			if (!elem || typeof elem.type === 'undefined') {
				enhancedElem = <React.Fragment key={idx}>{elem}</React.Fragment>;
			} else if (typeof elem.type === 'string') {
				enhancedElem = React.cloneElement(elem, { key: idx, children });
			} else {
				enhancedElem = React.cloneElement(elem, { key: idx, ...propsForChildren, children });
			}

			return enhancedElem;
		}
	}

	//  *******************************************
	//  Exposed methods to help advanced use cases
	//  *******************************************

	getFormData() {
		return this.form.current.getCurrentValues();
	}

	submit(formData) {
		return this.form.current.submit(formData);
	}

	reset() {
		this.form.current.reset();
	}

	render() {
		return (
			<Formsy
				id={this.props.formName}
				className={cx('esri-form', this.props.className, this.props.darkTheme || this.props.IsDarkMode ? 'dark-theme' : null)}
				ref={this.form}
				onValidSubmit={this.handleSubmit}
				onInvalidSubmit={this.handleInvalidSubmit}
				onValid={this.handleValid}
				onInvalid={this.handleInvalid}
				onChange={this.handleChange}>
				<HiddenInput name="btst" />
				{this.renderChildren()}
			</Formsy>
		);
	}
}

EsriForm.propTypes = {
	formName: PropTypes.string,
	analyticsProps: PropTypes.object,
	disableAnalytics: PropTypes.bool,
	pardotDefaultFormHandler: PropTypes.string,
	pardotSFIDFormHandlerLookup: PropTypes.array,
	forceWebNodeSubmission: PropTypes.bool,
	isMql: PropTypes.bool,
	className: PropTypes.string,
	darkTheme: PropTypes.bool,
	onFormLoad: PropTypes.func,
	onFormStart: PropTypes.func,
	onValid: PropTypes.func,
	onInvalid: PropTypes.func,
	onInvalidSubmit: PropTypes.func,
	onChange: PropTypes.func,
	onSubmit: PropTypes.func,
	onSubmitError: PropTypes.func,
	onAfterSubmit: PropTypes.func,
};

export default EsriForm;
